import type { ReactNode } from 'react';
import { Receipt } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useInvoiceV2Tracking, useTrackView } from '../../hooks/useInvoiceV2Tracking';
import { INVOICE_V2_ROUTES } from '../constants/routes';
import { useOpenInvoicePlans } from '../hooks/useInvoiceQuota';
import {
  isPlanChangeUnavailable,
  isQuotaExhausted,
  quotaCopyOf,
  quotaSnapshotOf,
  quotaUsedOf,
  type InvoiceQuota,
} from '../lib/quota';
import { InvoiceButton } from './InvoiceButton';
import { PlanChangeUnavailable } from './PlanChangeUnavailable';
import { InvoiceCard } from './SummaryCard';

/**
 * The month's quota as a card: how many invoices are left (or that none are) and
 * when they renew. The wizard paywall mounts it; on the home the plan chip
 * (`PlanStatus`) says the same in one line.
 *
 * Without `onSeePlans` there is nowhere to upgrade and the card stays at the
 * consequence, unless the plan was bought and cannot change yet: then it says so
 * and offers «Habla con nosotros».
 */
export const QuotaCard = ({
  quota,
  note,
  onSeePlans,
  secondaryAction,
  className,
}: {
  quota: InvoiceQuota;
  /** Una línea más después de la renovación, como que el borrador no se pierde. */
  note?: string;
  onSeePlans?: () => void;
  secondaryAction?: ReactNode;
  className?: string;
}) => {
  const { headline, renewal } = quotaCopyOf(quota);
  const exhausted = isQuotaExhausted(quota);
  const limit = quota.window.limit ?? 0;
  const used = Math.min(quotaUsedOf(quota), limit);
  const detail = [renewal, note].filter(Boolean).join(' ');

  return (
    <InvoiceCard className={cn('flex flex-col gap-3', className)}>
      <div className='flex items-start gap-3'>
        <span
          className={cn(
            'flex size-9 flex-none items-center justify-center rounded-lg',
            exhausted ? 'bg-h-error-90' : 'bg-h-warning-90',
          )}>
          <Receipt size={18} weight='fill' className={exhausted ? 'text-h-error-50' : 'text-h-warning-50'} />
        </span>
        <div className='min-w-0 flex-1'>
          <p className='text-h-text-heading text-[15px] leading-snug font-semibold'>{headline}</p>
          {detail && <p className='text-h-text-medium mt-0.5 text-sm leading-snug'>{detail}</p>}
        </div>
      </div>
      {limit > 0 && (
        <Progress
          value={(used / limit) * 100}
          aria-label='Facturas del mes'
          className={cn(
            'bg-h-neutral-90 h-1.5',
            exhausted ? '[&>div]:bg-h-error-50' : '[&>div]:bg-h-warning-50',
          )}
        />
      )}
      {onSeePlans ? (
        <InvoiceButton fullWidth onClick={onSeePlans}>
          Ver planes
        </InvoiceButton>
      ) : (
        isPlanChangeUnavailable(quota) && <PlanChangeUnavailable plan={quota.plan} fullWidth />
      )}
      {secondaryAction}
    </InvoiceCard>
  );
};

/**
 * El wizard con el cupo agotado: en lugar del paso, el aviso, antes de que el
 * usuario capture algo que no se va a poder timbrar. El borrador no se toca
 * (lo sigue guardando `useInvoiceDraft`), así que salir al inicio no lo pierde:
 * ahí lo espera «Dejaste una factura a medias».
 */
export const QuotaPaywall = ({ quota }: { quota: InvoiceQuota }) => {
  const navigate = useNavigate();
  const { trackQuotaPaywallShown } = useInvoiceV2Tracking();
  const openPlans = useOpenInvoicePlans();
  const snapshot = quotaSnapshotOf(quota);

  useTrackView(`${snapshot.planCode}:${snapshot.limit}`, () => trackQuotaPaywallShown(snapshot));

  return (
    <div className='mx-auto w-full max-w-[480px] px-3 pt-6 pb-10 md:pt-10'>
      <QuotaCard
        quota={quota}
        note='Tu factura se queda guardada.'
        onSeePlans={quota.upgrade_options.length > 0 ? () => openPlans('paywall', snapshot) : undefined}
        secondaryAction={
          <InvoiceButton variant='secondary' fullWidth onClick={() => navigate(INVOICE_V2_ROUTES.HOME)}>
            Volver al inicio
          </InvoiceButton>
        }
      />
    </div>
  );
};
